
// src/pages/ProductDetails.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../layout/Layout";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  FaChevronLeft,
  FaChevronRight,
  FaHeart,
} from "react-icons/fa";
import { IoMdShareAlt } from "react-icons/io";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import RelatedCard from "../components/RelatedCard";
import api from "../supabase/axios";
import { toast } from "react-toastify";
import { Ecom } from "../analytics";
import { Helmet } from "react-helmet-async";
import { useCurrency } from "../supabase/CurrencyContext";
import { useLoading } from "../context/LoadingContext";
import LoginPromptModal from "../components/LoginPromptModal";

/* ────────────────────── helpers ────────────────────── */
const asBool = (v) =>
  v === true || v === "true" || v === 1 || v === "1" || v === "Y";

const get = (obj, keys, fallback = undefined) =>
  keys.reduce(
    (acc, k) => (acc && acc[k] != null ? acc[k] : undefined),
    obj
  ) ?? fallback;

const IMAGE_BASE =
  process.env.REACT_APP_IMAGE_BASE || process.env.REACT_APP_API_BASE_URL || "";

const normalizeImageUrl = (u) => {
  if (!u || typeof u !== "string") return "";
  if (/^(data:|blob:|https?:\/\/)/i.test(u)) return u;
  if (u.startsWith("/")) return `${IMAGE_BASE}${u}`;
  return `${IMAGE_BASE}/${u}`;
};

const pickImageUrl = (img) =>
  normalizeImageUrl(
    img?.image_url ||
    img?.imageurl ||
    img?.url ||
    img?.publicUrl ||
    img?.publicurl ||
    img?.Location ||
    img?.location ||
    img?.path
  );

const categoryName = (p) =>
  p?.categories?.name ||
  get(p, ["category", "name"]) ||
  get(p, ["categories", 0, "name"]) ||
  get(p, ["categories", 0, "Name"]) ||
  "Accessories";

/* ────────────────────── price helpers ────────────────────── */
const getSalePrice = (p) => {
  const base = Number(p?.Price ?? 0);
  const disc = Number(p?.DiscountPrice ?? 0);
  return disc > 0 && base - disc > 0 ? base - disc : base;
};

const formatINR = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: Number(val) % 1 === 0 ? 0 : 2,
  }).format(Number(val || 0));

/* ────────────────────── Razorpay helpers ────────────────────── */
// const API = process.env.REACT_APP_API_BASE_URL || "";

// async function loadRazorpay() {
//   if (typeof window !== "undefined" && window.Razorpay) return true;
//   return new Promise((resolve) => {
//     try {
//       const s = document.createElement("script");
//       s.src = "https://checkout.razorpay.com/v1/checkout.js";
//       s.onload = () => resolve(true);
//       s.onerror = () => resolve(false);
//       document.body.appendChild(s);
//     } catch {
//       resolve(false);
//     }
//   });
// }
// const generateToken = () =>
//   `TXN-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

/* ────────────────────── UI components ────────────────────── */
const NextArrow = ({ onClick }) => (
  <button
    type="button"
    className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer z-10 bg-white/80 rounded-full p-2 shadow border border-[#D4A5A5] hover:bg-white"
    onClick={onClick}
    aria-label="Next image"
  >
    <FaChevronRight size={18} className="text-[#6B4226]" />
  </button>
);
const PrevArrow = ({ onClick }) => (
  <button
    type="button"
    className="absolute left-4 top-1/2 -translate-y-1/2 cursor-pointer z-10 bg-white/80 rounded-full p-2 shadow border border-[#D4A5A5] hover:bg-white"
    onClick={onClick}
    aria-label="Previous image"
  >
    <FaChevronLeft size={18} className="text-[#6B4226]" />
  </button>
);
const QuantitySelect = ({ max = 10, value, onChange }) => (
  <select
    aria-label="Select quantity"
    className="border border-[#D4A5A5] rounded-md px-3 py-2 bg-white"
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
  >
    {Array.from({ length: Math.min(max, 10) }, (_, i) => i + 1).map((n) => (
      <option key={n} value={n}>
        Qty: {n}
      </option>
    ))}
  </select>
);
const StarRating = ({ value = 0, size = 18 }) => {
  const full = Math.floor(value);
  const stars = Array.from({ length: 5 }, (_, i) =>
    i < full ? "full" : "empty"
  );
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`Rating: ${value} out of 5`}
    >
      {stars.map((t, i) =>
        t === "full" ? (
          <AiFillStar key={i} size={size} className="text-[#D4A5A5]" />
        ) : (
          <AiOutlineStar key={i} size={size} className="text-[#D4A5A5]" />
        )
      )}
    </span>
  );
};

/* ────────────────────── MAIN COMPONENT ────────────────────── */
const ProductDetails = () => {
  // const { currency = "USD", loading: currencyLoading = true } = useCurrency() || {};
  const {
    currency = "USD",
    loading: currencyLoading = true,
    convertFromINR,
    baseCurrency = "INR",
  } = useCurrency() || {};
  const { setLoading } = useLoading();                 // only global loader
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [error, setError] = useState(null);
  const [qty, setQty] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [wishlistSubmitting, setWishlistSubmitting] = useState(false);
  const [buyNowSubmitting, setBuyNowSubmitting] = useState(false);
  const sliderRef = useRef();
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [zoomScale, setZoomScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [didDrag, setDidDrag] = useState(false);
  const navigate = useNavigate();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get("/api/auth/me");
        setUser(res.data.user);
      } catch (err) {
        setUser(null); // Guest user
      }
    };
    fetchUser();
  }, []);

  const handleLogin = () => {
    setLoginModalOpen(false);
    navigate("/account");
  };


  // thank‑you modal
  const [thankOpen, setThankOpen] = useState(false);
  const [thankOrderNo, setThankOrderNo] = useState("");
  const [emailNoted, setEmailNoted] = useState(false);

  const isUpcoming = product?.LaunchingDate
    ? new Date(product.LaunchingDate) > new Date()
    : false;

  const salePriceINR = product
    ? getSalePrice(product)
    : 0;

  const convertedPrice = convertFromINR
    ? convertFromINR(salePriceINR)
    : salePriceINR;

  const formatPrice = (value, currencyCode) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const showBaseNote =
    currency &&
    baseCurrency &&
    currency !== baseCurrency;

  /* ────────────────────── FETCH PRODUCT ────────────────────── */
  const fetchProduct = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: p } = await api.get(`/api/products/${id}`);
      setProduct(p);

      const cat = categoryName(p);
      const { data: rel } = await api.get(
        `/api/products?category=${encodeURIComponent(cat)}&limit=8`
      );
      setRelatedProducts(
        (rel || []).filter((rp) => String(rp.id) !== String(id))
      );

      document.title = `${p.Name} - Shahu Mumbai`;

      try {
        Ecom.viewItem({
          id: p.ProductId,
          title: p.Name,
          category: cat,
          price: getSalePrice(p),
          quantity: 1,
          currency: "INR",
        });
      } catch { }
    } catch (err) {
      setError("Failed to fetch product details. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const res = await api.get(`/api/reviews/${id}`);
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setReviews(list);
      const count = list.length;
      const avg = count
        ? list.reduce((s, r) => s + Number(r.Rating || 0), 0) / count
        : 0;
      setReviewCount(count);
      setAvgRating(Number(avg.toFixed(1)));
    } catch {
      // silent – reviews are optional
    }
  };

  const checkWishlist = async () => {
    if (!user) {
      setIsInWishlist(false);
      return;
    }
    try {
      const { data } = await api.get("/api/wishlist");
      const isWishlisted = (data.data || []).some(
        (item) => String(item.product_id) === String(id)
      );
      setIsInWishlist(isWishlisted);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to check wishlist");
    }
  };

  useEffect(() => {
    if (!currencyLoading) {
      fetchProduct();
      fetchReviews();
      checkWishlist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user, currencyLoading]);

  /* ────────────────────── WISHLIST ────────────────────── */
  const handleToggleWishlist = async () => {
    if (!user) {
      toast.error("Please log in to manage your wishlist");
      return;
    }
    if (wishlistSubmitting) return;

    try {
      setWishlistSubmitting(true);
      if (isInWishlist) {
        const { data } = await api.get("/api/wishlist");
        const item = (data.data || []).find(
          (it) => String(it.product_id) === String(id)
        );
        if (item) {
          await api.delete(`/api/wishlist/${item.id}`);
          setIsInWishlist(false);
          toast.success("Removed from wishlist");
        }
      } else {
        await api.post("/api/wishlist", { product_id: id });
        setIsInWishlist(true);
        toast.success("Added to wishlist");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update wishlist");
    } finally {
      setWishlistSubmitting(false);
    }
  };

  /* ────────────────────── REVIEW FORM ────────────────────── */
  const [reviewName, setReviewName] = useState("");
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cartSubmitting, setCartSubmitting] = useState(false);

  useEffect(() => {
    if (user?.fullname) {
      setReviewName(user.fullname);
    } else {
      setReviewName("");
    }
  }, [user]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!reviewText.trim()) return;
    // if (!user) {
    //   toast.info("Please login to submit a review.");
    //   return;
    // }

    // if (!user) {
    //   const goLogin = window.confirm(
    //     "Please login to submit a review."
    //   );

    //   if (goLogin) {
    //     navigate("/login");
    //   }

    //   return;
    // }

    if (!user) {
      setPendingAction("review");
      setLoginModalOpen(true);
      return;
    }
    try {
      setSubmitting(true);
      await api.post(`/api/reviews`, {
        rating: Number(reviewRating),
        productid: id,
        userid: parseInt(user.id),
        comment: reviewText.trim(),
      });
      toast.success("Review submitted successfully!");
      fetchReviews();
    } catch (e2) {
      toast.error(e2.response?.data?.message || "Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ────────────────────── ADD TO CART ────────────────────── */
  const handleAddToCart = async () => {
    setLoading(true);
    if (Number(product.Stock) === 0 || cartSubmitting) return;
    try {
      setCartSubmitting(true);
      await api.post("/api/cart", {
        product_id: product.ProductId,
        quantity: qty,
      });
      toast.success(`${product.Name} added to cart!`);
      window.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { delta: qty } })
      );
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to add to cart.");
    } finally {
      setCartSubmitting(false);
      setLoading(false);
    }
  };


  /* ────────────────────── BUY NOW (Razorpay) ────────────────────── */
  const handleBuyNow = async () => {
    if (!product) return;

    if (Number(product.Stock) === 0) {
      toast.error("This product is out of stock.");
      return;
    }

    try {
      setLoading(true);
      setBuyNowSubmitting(true);

      // Option 1: Just add to cart (will accumulate if already in cart)
      await api.post("/api/cart", {
        product_id: product.ProductId,
        quantity: qty,
      });

      // Optional: fire a small tracking event
      try {
        Ecom.addPaymentInfo(
          [
            {
              id: product.ProductId,
              title: product.Name,
              category: categoryName(product),
              price: getSalePrice(product),
              quantity: qty,
              currency: "INR",
            },
          ],
          "buy_now"
        );
      } catch {
        // ignore analytics errors
      }

      // Go to checkout – Checkout will load cart and show correct total
      navigate("/checkout", {
        state: {
          source: "buy_now",
          productId: product.ProductId,
          quantity: qty,
        },
      });
    } catch (err) {
      console.error("Buy Now error:", err);
      toast.error(
        err?.response?.data?.error || "Could not start checkout. Please try again."
      );
    } finally {
      setBuyNowSubmitting(false);
      setLoading(false);
    }
  };

  /* ────────────────────── SEO ────────────────────── */
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://www.shahumumbai.com";
  const canonical = `${baseUrl}/products/${id}`;
  const metaDescription = (product?.shortdescription || product?.description || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 300);

  /* ────────────────────── EARLY RETURNS ────────────────────── */
  if (error) {
    return (
      <Layout>
        <Helmet>
          <title>Error - Shahu Mumbai</title>
        </Helmet>
        <Helmet>
          <title>Error - Shahu Mumbai</title>
        </Helmet>
        <div className="min-h-screen flex items-center justify-center bg-[#F1E7E5]">
          <p className="p-6 text-center text-red-500">{error}</p>
        </div>
      </Layout>
    );
  }

  const mobileSliderSettings = {
    dots: true,
    arrows: false,
    infinite: true,
    speed: 400,
    slidesToShow: 1,
    slidesToScroll: 1,
  };
  const imgs = Array.isArray(product?.product_images)
    ? product.product_images
    : [];

  const ordered = [
    ...imgs.filter((i) => asBool(i?.is_hero)).map(pickImageUrl),
    ...imgs.filter((i) => !asBool(i?.is_hero)).map(pickImageUrl),
  ].filter(Boolean);

  const images = [...new Set(ordered)];
  /* -------------------------------------------------
   *  IMPORTANT: Render Layout *immediately* so the
   *  background colour exists while the spinner shows.
   * ------------------------------------------------- */


  return (
    <Layout>
      <Helmet>
        <title>{product ? `${product.Name} — Shahu Mumbai` : "Loading…"}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        {/* other meta tags … */}
      </Helmet>

      {/* ────── SKELETON WHILE LOADING ────── */}
      {!product ? (
        <div className="min-h-screen bg-[#F1E7E5] px-4 md:px-6 py-16 pt-[60px] font-serif">
          {/* Breadcrumb skeleton */}
          <div className="max-w-6xl mx-auto mb-6 h-6 bg-gray-200 rounded animate-pulse" />

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Thumbs */}
            <div className="lg:col-span-2">
              <div className="flex lg:flex-col gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-20 h-20 bg-gray-200 rounded-md animate-pulse"
                  />
                ))}
              </div>
            </div>

            {/* Gallery */}
            <div className="lg:col-span-6">
              <div className="h-[70vh] bg-gray-200 rounded-lg animate-pulse" />
            </div>

            {/* Buy box */}
            <div className="lg:col-span-4">
              <div className="space-y-4">
                <div className="h-8 bg-gray-200 rounded animate-pulse" />
                <div className="h-6 bg-gray-200 rounded animate-pulse" />
                <div className="h-12 bg-gray-200 rounded animate-pulse" />
                <div className="h-12 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      ) : isUpcoming ? (
        /* ────── COMING SOON ────── */
        <div className="min-h-screen flex items-center justify-center bg-[#F1E7E5] font-serif">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#6B4226] mb-4">
              Coming Soon
            </h1>
            <p className="text-lg text-[#3E2C23]">
              {product.Name} will be available soon!
            </p>
            <p className="text-sm text-[#3E2C23] mt-2">
              Launching on:{" "}
              {new Date(product.LaunchingDate).toLocaleString("en-IN", {
                dateStyle: "medium",
                timeStyle: "short",
                timeZone: "Asia/Kolkata",
              })}
            </p>
          </div>
        </div>
      ) : (
        /* ────── FULL PRODUCT UI ────── */
        <div className="min-h-screen bg-[#F1E7E5] font-serif">
          {/* Breadcrumb */}
          <nav className="max-w-6xl mx-auto text-sm mb-4 text-[#6B4226]">
            <ol className="flex flex-wrap gap-1">
              <li>
                <Link to="/" className="hover:underline">
                  Home
                </Link>
                <span className="mx-2 text-[#D4A5A5]">/</span>
              </li>
              <li>
                <Link to="/products" className="hover:underline">
                  Our Products
                </Link>
                <span className="mx-2 text-[#D4A5A5]">/</span>
              </li>
              <li className="text-[#3E2C23] truncate max-w-[60%]" title={product.Name}>
                {product.Name}
              </li>
            </ol>
          </nav>

          {/* ────── MAIN LAYOUT ────── */}

          {/* ────── MAIN LAYOUT (HERMES STYLE) ────── */}

          {(() => {
            // const imgs = Array.isArray(product.product_images)
            //     ? product.product_images
            //     : [];

            // const ordered = [
            //     ...imgs.filter((i) => asBool(i?.is_hero)).map(pickImageUrl),
            //     ...imgs.filter((i) => !asBool(i?.is_hero)).map(pickImageUrl),
            // ].filter(Boolean);

            // const images = [...new Set(ordered)];


            return (

              // <div className="relative w-screen left-1/2 -translate-x-1/2">
              <div className="-mx-4 sm:-mx-6 lg:-mx-8 relative">

                {/* IMAGES */}


                {/* DESKTOP (Hermes columns) */}
                {/* DESKTOP HERMES DRAG GALLERY */}

                <div
                  className="
hidden
lg:flex
w-full
h-[100vh]
overflow-x-auto
overflow-y-hidden
cursor-grab
active:cursor-grabbing
relative
"

                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none"
                  }}

                  onMouseDown={(e) => {

                    const slider = e.currentTarget;

                    slider.dataset.dragging = "true";

                    slider.dataset.startx = e.pageX;

                    slider.dataset.left = slider.scrollLeft;

                    setDidDrag(false);

                  }}

                  onMouseMove={(e) => {

                    const slider = e.currentTarget;

                    if (slider.dataset.dragging !== "true") return;

                    e.preventDefault();

                    const x = e.pageX;

                    const delta =
                      x - Number(slider.dataset.startx);

                    if (Math.abs(delta) > 10) {

                      setDidDrag(true);

                    }

                    slider.scrollLeft =
                      Number(slider.dataset.left) - delta;

                  }}

                  onMouseUp={(e) => {

                    e.currentTarget.dataset.dragging = "false";

                    setTimeout(() => {

                      setDidDrag(false);

                    }, 50);

                  }}

                  onMouseLeave={(e) => {

                    e.currentTarget.dataset.dragging = "false";

                  }}
                >

                  {/* IMAGE TRACK */}

                  <div className="flex h-full flex-shrink-0">

                    {images.map((img, i) => (

                      <div
                        key={i}
                        className="
w-[46vw]
h-full
flex-shrink-0
"
                      >

                        <img
                          src={img}
                          alt={`view ${i}`}
                          draggable={false}

                          className="
  w-full
  h-full
  object-cover
  select-none
  cursor-grab
  "

                          onClick={(e) => {

                            if (didDrag) {

                              e.preventDefault();

                              return;

                            }

                            setZoomIndex(i);

                            setZoomOpen(true);

                            setZoomScale(2.5);

                            setPosition({
                              x: 0,
                              y: -80
                            });

                          }}

                          onError={(e) => {

                            e.currentTarget.src =
                              `${process.env.PUBLIC_URL}/assets/images/placeholder.png`;

                          }}
                        />

                      </div>

                    ))}

                  </div>



                  {/* KEEP YOUR EXISTING BUY BOX CONTENT HERE */}

                </div>



                {/* MOBILE (Slider) */}
                <div className="lg:hidden">
                  <Slider {...mobileSliderSettings}>
                    {images.map((img, i) => (
                      <div key={i}>
                        <img
                          src={img}
                          alt={`view ${i}`}
                          className="w-full h-[60vh] object-cover"
                          onClick={() => {
                            setZoomIndex(i);
                            setZoomOpen(true);
                            setZoomScale(1);
                          }}
                          onError={(e) => {
                            e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/images/placeholder.png`;
                          }}
                        />
                      </div>
                    ))}
                  </Slider>
                </div>

                {/* FLOATING BUY BOX */}
                <div className="
        w-full 
        lg:absolute 
        lg:top-[48%] 
        lg:right-[5%] 
        lg:w-[300px] 
        bg-white/95 
        backdrop-blur-md p-6 
        rounded-xl 
        shadow-[0_20px_60px_rgba(0,0,0,0.15)]
        mt-4 lg:mt-0
      ">

                  <h1 className="text-2xl font-bold text-[#6B4226] mb-1">
                    {product.Name}
                  </h1>

                  {/* <div className="flex items-center gap-2 mt-1">
                    <StarRating value={avgRating} />
                    <span className="text-xs text-[#3E2C23]">
                      {avgRating}/5 • {reviewCount} reviews
                    </span>
                  </div> */}

                  {/* <p className="text-sm italic text-[#A3B18A] mt-1">
                    Category: {categoryName(product)}
                  </p> */}

                  {/* PRICE */}
                  {/* <p className="text-3xl font-extrabold text-[#6B4226] mt-4">
                    {formatINR(getSalePrice(product))}
                  </p> */}
                  <div className="mt-4">
                    <p className="text-3xl font-extrabold text-[#6B4226]">
                      {formatPrice(
                        convertedPrice,
                        currency || baseCurrency
                      )}
                    </p>

                    {showBaseNote && (
                      <p className="text-xs text-[#3E2C23]/70 mt-1">
                        Approx.{" "}
                        {formatPrice(salePriceINR, baseCurrency)}
                        {" "} (charged in {baseCurrency})
                      </p>
                    )}
                  </div>

                  {/* QTY */}
                  <div className="mt-4 flex items-center gap-3">
                    <QuantitySelect
                      max={Math.min(10, Number(product.Stock) || 10)}
                      value={qty}
                      onChange={setQty}
                    />
                    {/* <p className="text-sm text-[#A3B18A]">
                      In Stock: {product.Stock}
                    </p> */}
                  </div>

                  {/* BUTTONS SIDE BY SIDE */}
                  <div className="mt-5 flex gap-3">
                    <button
                      onClick={handleAddToCart}
                      className="flex-1 bg-black text-white py-3 rounded-md font-semibold"
                    >
                      Add to Cart
                    </button>

                    <button
                      onClick={handleBuyNow}
                      className="flex-1 bg-black text-white py-3 rounded-md font-semibold"
                    >
                      Buy Now
                    </button>
                  </div>

                </div>
              </div>
            );
          })()}


          {/* ────── DESCRIPTION & SPECS ────── */}
          <section className="max-w-6xl mx-auto mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <div className="rounded-lg border border-[#D4A5A5] shadow bg-white p-6">
                <h2 className="text-xl font-bold text-[#6B4226] mb-3">
                  Product Description
                </h2>
                <p className="text-base text-[#3E2C23] leading-relaxed whitespace-pre-line">
                  {product.Description || "No additional description provided."}
                </p>
                {product.BrandDesigner && (
                  <div className="mt-4 text-md text-[#6B4226]">
                    Designer:{" "}
                    <span className="font-semibold">{product.BrandDesigner}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-4 space-y-6">

              {/* Specifications */}
              <div className="rounded-lg border border-[#D4A5A5] shadow bg-white p-6">

                <h3 className="text-lg font-bold text-[#6B4226] mb-2">
                  Specifications
                </h3>

                <dl className="text-sm text-[#3E2C23] grid grid-cols-1 gap-2">

                  <div className="flex justify-between border-b border-[#F1E7E5] pb-2">
                    <dt>Category</dt>
                    <dd className="font-medium">
                      {categoryName(product)}
                    </dd>
                  </div>

                  <div className="flex justify-between">
                    <dt>Stock</dt>
                    <dd className="font-medium">
                      {Number(product.Stock) > 0
                        ? product.Stock
                        : "Out of stock"}
                    </dd>
                  </div>

                </dl>

              </div>

              {/* About this item */}
              <div className="rounded-lg border border-[#D4A5A5] shadow bg-white p-6">

                <h3 className="text-lg font-bold text-[#6B4226] mb-3">
                  About this item
                </h3>

                <ul className="list-disc list-inside text-sm text-[#3E2C23] space-y-2">

                  {product.ShortDescription ? (
                    String(product.ShortDescription)
                      .split(/\.|\n|\r/)
                      .map((s) => s.trim())
                      .filter(Boolean)
                      .slice(0, 5)
                      .map((point, i) => (
                        <li key={i}>{point}</li>
                      ))
                  ) : (
                    <li>Premium quality and crafted design.</li>
                  )}

                </ul>

              </div>

            </div>


          </section>

          {/* ────── REVIEWS ────── */}
          <section className="max-w-6xl mx-auto mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8">
              <div className="rounded-lg border border-[#D4A5A5] shadow bg-white p-3 xs:p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2 xs:mb-3 sm:mb-4">
                  <h2 className="text-lg xs:text-xl font-bold text-[#6B4226]">
                    Customer Reviews
                  </h2>
                  <div className="flex items-center gap-1 xs:gap-2">
                    <StarRating value={avgRating} />
                    <span className="text-xs xs:text-sm text-[#3E2C23]">
                      {avgRating}/5 • {reviewCount} review{reviewCount === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>

                {reviews.length === 0 ? (
                  <p className="text-[#3E2C23] text-sm xs:text-base">
                    No reviews yet. Be the first to review!
                  </p>
                ) : (
                  <ul className="space-y-2 xs:space-y-3 sm:space-y-4">
                    {reviews.map((r) => (
                      <li
                        key={
                          r.ReviewId ||
                          `${r.userid}-${r.productid}-${r.CreatedAt ||
                          r.created_at ||
                          Math.random()}`
                        }
                        className="border border-[#F1E7E5] rounded-lg p-3 xs:p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1 xs:gap-2">
                            <StarRating
                              value={Number(r.Rating || r.rating) || 0}
                            />
                            <span className="text-xs xs:text-sm text-[#6B4226] font-semibold">
                              {r.FullName ||
                                r?.users?.full_name ||
                                "Anonymous"}
                            </span>
                          </div>
                          <time className="text-[10px] xs:text-xs text-[#3E2C23] opacity-70">
                            {r.CreatedAt || r.created_at
                              ? new Date(
                                r.CreatedAt || r.created_at
                              ).toLocaleDateString("en-IN")
                              : ""}
                          </time>
                        </div>
                        <p className="mt-1 xs:mt-2 text-xs xs:text-sm text-[#3E2C23] leading-relaxed whitespace-pre-line">
                          {r.Comment || r.comment}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Review Form */}
            <div className="lg:col-span-4">
              <form
                onSubmit={submitReview}
                className="rounded-lg border border-[#D4A5A5] shadow bg-white p-6"
              >
                <h3 className="text-lg font-bold text-[#6B4226] mb-3">
                  Write a review
                </h3>

                <label className="block text-sm text-[#3E2C23] mb-1">
                  Your name (optional)
                </label>
                <input
                  type="text"
                  value={reviewName}
                  onChange={(e) => setReviewName(e.target.value)}
                  className="w-full border border-[#D4A5A5] rounded-md px-3 py-2 mb-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A5A5]"
                  placeholder="Enter your name"
                  readOnly={!!user}
                />

                <label className="block text-sm text-[#3E2C23] mb-1">
                  Rating
                </label>
                <select
                  value={reviewRating}
                  onChange={(e) => setReviewRating(Number(e.target.value))}
                  className="w-full border border-[#D4A5A5] rounded-md px-3 py-2 mb-3 bg-white"
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>
                      {n} -{" "}
                      {n === 5
                        ? "Excellent"
                        : n === 4
                          ? "Good"
                          : n === 3
                            ? "Average"
                            : n === 2
                              ? "Poor"
                              : "Terrible"}
                    </option>
                  ))}
                </select>

                <label className="block text-sm text-[#3E2C23] mb-1">
                  Review
                </label>
                <textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  className="w-full min-h-[120px] border border-[#D4A5A5] rounded-md px-3 py-2 mb-4 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4A5A5]"
                  placeholder="Share what you liked or what could be improved…"
                  required
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full bg-[#6B4226] text-white px-6 py-3 rounded-md font-semibold shadow hover:opacity-90 transition ${submitting ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                >
                  {submitting ? "Submitting…" : "Submit Review"}
                </button>
              </form>
            </div>
          </section>

          {/* ────── RELATED PRODUCTS ────── */}
          <div className="mt-16 px-2 max-w-[1440px] mx-auto font-serif">
            <h2 className="text-2xl font-bold text-[#6B4226] mb-6 text-center">
              You May Also Like
            </h2>
            <div className="flex flex-wrap justify-center gap-8">
              {relatedProducts.map((related) => {
                const rImgs = Array.isArray(related.product_images)
                  ? related.product_images
                  : [];
                const ordered = [
                  ...rImgs.filter((i) => asBool(i?.is_hero)).map(pickImageUrl),
                  ...rImgs.filter((i) => !asBool(i?.is_hero)).map(pickImageUrl),
                ].filter(Boolean);
                const img = ordered[0] || "/assets/images/placeholder.png";
                const sale =
                  Number(related.DiscountPrice) > 0 &&
                    Number(related.DiscountPrice) < Number(related.Price)
                    ? Number(related.Price) - Number(related.DiscountPrice)
                    : Number(related.Price);

                return (
                  <div
                    key={related.id || related.ProductId}
                    className="w-[260px]"
                  >
                    <RelatedCard
                      product={{
                        id: related.id || related.ProductId,
                        name: related.Name || related.name,
                        price: sale,
                        currency: "INR",
                        image: img,
                        category: categoryName(related),
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* ────── THANK‑YOU MODAL ────── */}
          {thankOpen && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center space-y-4 shadow-xl">
                <h2 className="text-xl font-semibold text-green-600">
                  Payment Successful
                </h2>
                <p className="text-gray-700">Your order has been placed.</p>
                <p className="text-sm font-mono text-gray-600">
                  Order Number: <strong>{thankOrderNo}</strong>
                </p>
                <p className="text-xs text-gray-600">
                  {emailNoted
                    ? "A confirmation email has been sent."
                    : "We’ll email your receipt shortly."}
                </p>
                <div className="flex gap-3 justify-center">
                  <Link
                    to="/myorder"
                    className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
                  >
                    View Orders
                  </Link>
                  <button
                    onClick={() => setThankOpen(false)}
                    className="px-4 py-2 rounded border"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {zoomOpen && (

            <div
              className="
fixed inset-0
bg-[radial-gradient(circle_at_center,#1b1b1b_0%,#0c0c0c_60%,#050505_100%)]
z-[9999]
overflow-hidden
animate-[fadeIn_.35s_ease]
"
            >

              {/* CLOSE */}

              <button
                onClick={() => setZoomOpen(false)}
                className="
absolute top-6 right-6
text-white/90
hover:text-white
text-5xl
z-50
transition-all
duration-300
"
              >
                ×
              </button>

              {/* LEFT ZOOM BAR */}

              <div
                className="
absolute left-8
top-1/2
-translate-y-1/2
z-50
hidden md:block
"
              >

                <div
                  className="
bg-white/10
backdrop-blur-xl
border border-white/10
rounded-full
px-4 py-6
flex flex-col items-center gap-5
"
                >

                  <button
                    onClick={() =>
                      setZoomScale(s => Math.min(4, s + .25))
                    }
                    className="text-white text-3xl"
                  >
                    +
                  </button>

                  <div className="relative w-[2px] h-[220px] bg-white/20">

                    <div
                      className="
absolute
w-4 h-4
bg-white
rounded-full
left-1/2
-translate-x-1/2
shadow-lg
"
                      style={{
                        top: `${220 - ((zoomScale - 1) / 3) * 220}px`
                      }}
                    />

                  </div>

                  <button
                    onClick={() =>
                      setZoomScale(s => Math.max(1, s - .25))
                    }
                    className="text-white text-3xl"
                  >
                    −
                  </button>

                </div>

              </div>

              {/* IMAGE AREA */}

              <div
                className="
w-screen
h-screen
flex
items-center
justify-center
overflow-hidden
"

                onWheel={(e) => {

                  e.preventDefault();

                  setZoomScale(prev =>
                    Math.min(
                      4,
                      Math.max(
                        1,
                        prev + (e.deltaY < 0 ? .15 : -.15)
                      )
                    )
                  );

                }}

                onMouseMove={(e) => {

                  if (!dragging) return;

                  setPosition({
                    x: e.clientX - startPos.x,
                    y: e.clientY - startPos.y
                  });

                }}

                onMouseUp={() => setDragging(false)}
                onMouseLeave={() => setDragging(false)}
              >

                <img
                  src={images[zoomIndex]}
                  alt="Zoom"

                  draggable={false}

                  onMouseDown={(e) => {

                    if (zoomScale <= 1) return;

                    setDragging(true);

                    setStartPos({
                      x: e.clientX - position.x,
                      y: e.clientY - position.y
                    });

                  }}

                  className="
w-auto
max-w-[94vw]
max-h-[92vh]
object-contain
select-none
cursor-grab
active:cursor-grabbing
drop-shadow-[0_40px_120px_rgba(0,0,0,.75)]
"

                  style={{

                    transform: `
translate(
${position.x}px,
${position.y}px
)
scale(${zoomScale})
`,

                    transformOrigin: "center center",

                    transition: dragging
                      ? "none"
                      : "transform .45s cubic-bezier(.22,1,.36,1)"

                  }}
                />

              </div>

              {/* ARROWS */}

              <button
                onClick={() => {

                  setZoomIndex(
                    prev =>
                      (prev - 1 + images.length) % images.length
                  );

                  setZoomOpen(true);

                  setZoomScale(2.5);

                  setPosition({
                    x: 0,
                    y: -80
                  });
                }}
                className="
absolute
left-6
top-1/2
-translate-y-1/2
text-white/60
hover:text-white
text-7xl
transition-all
duration-300
"
              >
                ‹
              </button>

              <button
                onClick={() => {

                  setZoomIndex(
                    prev =>
                      (prev - 1 + images.length) % images.length
                  );

                  setZoomOpen(true);

                  setZoomScale(2.5);

                  setPosition({
                    x: 0,
                    y: -80
                  });

                }}
                className="
absolute
right-6
top-1/2
-translate-y-1/2
text-white/60
hover:text-white
text-7xl
transition-all
duration-300
"
              >
                ›
              </button>

              {/* THUMBNAILS */}

              <div
                className="
absolute
left-8
bottom-8
z-50
"
              >

                <div className="flex gap-3">

                  {images.map((img, i) => (

                    <img
                      key={i}
                      src={img}

                      onClick={() => {

                        setZoomIndex(i);

                        setZoomScale(1);

                        setPosition({
                          x: 0,
                          y: 0
                        });

                      }}

                      className={`
w-16
h-16
object-cover
rounded-sm
cursor-pointer
transition-all
duration-500

${zoomIndex === i
                          ?
                          "ring-2 ring-white opacity-100 scale-105"
                          :
                          "opacity-55 hover:opacity-100"
                        }
`}
                    />

                  ))}

                </div>

              </div>

            </div>

          )}
        </div>
      )}

      <LoginPromptModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onLogin={handleLogin}
      />

    </Layout>


  );
};


export default ProductDetails;
